import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Plus, FolderOpen, Search, X, ChevronDown, ChevronRight, File, FilePlus, LucideFileTerminal, FileCog, FileText, MoveRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useProjectStore } from '@/stores/useProject.store.js';
import InfiniteScrollPagination from '../InfiniteScroll';
import { appAxios } from '@/network/base/appAxios';
import PermitProjectDetail from '../PermitProjectDetail';
import { useDebouncedValue } from '@/customHooks/useDebouncedValue';

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { permitChatId, permitId } = useParams();

  const setActivePermitChatID = useProjectStore(state => state.setActivePermitChatID);
  const unassignedChats = useProjectStore(state => state.unassignedChats);
  const unassignedLoading = useProjectStore(state => state.unassignedLoading);
  const hasMoreUnassigned = useProjectStore(state => state.hasMoreUnassigned);
  const loadMoreUnassigned = useProjectStore(state => state.loadMoreUnassigned);

  const PAGE_SIZE = 6;

  // Grouped Projects 
  const [groupedProjects, setGroupedProjects] = useState([]);
  const [projectsPage, setProjectsPage] = useState(1);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [hasMoreProjects, setHasMoreProjects] = useState(true);

  // Expand/Collapse States
  const [expandedProjectId, setExpandedProjectId] = useState(null);
  const [expandedChatId, setExpandedChatId] = useState(null);
  const [isUnassignedCollapsed, setIsUnassignedCollapsed] = useState(false);
  const [isProjectsCollapsed, setIsProjectsCollapsed] = useState(false);



  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 300);
  const [pendingChatId, setPendingChatId] = useState(null);

  const navigationTimerRef = useRef(null);
  const lastNavigationTimeRef = useRef(0);
  const NAVIGATION_THROTTLE = 500;

  const navItems = [
    { path: '/citypermit/chat', label: 'New chat', icon: FilePlus },
    // { path: '/citypermit/new-project', label: 'New project', icon: FileCog },
    // { path: '/citypermit/new-project', label: 'Project History', icon: FileText },
    { path: '/citypermit/project', label: 'Project History', icon: FileText },
  ];

  const loadMoreGroupedProjects = async () => {
    if (projectsLoading) return;
    setProjectsLoading(true);
    try {
      const response = await appAxios.get(`/ProtectedPermitChat/v1.0/getGroupedChats?page=${projectsPage}&limit=${PAGE_SIZE}`);
      const { projects, pagination } = response.data.data;
      setGroupedProjects(prev => {
        const merged = [...prev];
        projects.forEach(project => {
          const existingIndex = merged.findIndex(p => p.projectId === project.projectId);
          if (existingIndex >= 0) {
            const existingChatIds = new Set(merged[existingIndex].chats.map(c => c.chatId));
            const newChats = project.chats.filter(c => !existingChatIds.has(c.chatId));
            merged[existingIndex] = {
              ...merged[existingIndex],
              chats: [...merged[existingIndex].chats, ...newChats],
            };
          } else {
            merged.push(project);
          }
        });
        return merged;
      });
      setProjectsPage(prev => prev + 1);
      setHasMoreProjects(pagination.page < pagination.totalPages);
    } catch (error) {
      console.error('Failed to fetch grouped projects:', error);
    } finally {
      setProjectsLoading(false);
    }
  };


  useEffect(() => {
    loadMoreGroupedProjects();
  }, []);

  useEffect(() => {
    loadMoreUnassigned(debouncedSearchQuery.trim() || null, true);
  }, [debouncedSearchQuery]);

  useEffect(() => {
    return () => {
      if (navigationTimerRef.current) {
        clearTimeout(navigationTimerRef.current);
        navigationTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (permitChatId && pendingChatId === permitChatId) {
      setPendingChatId(null);
    }
  }, [permitChatId, pendingChatId]);

  const openChat = (chat, isProjectClick = false) => {
    if (!permitId && permitChatId) {
      if (permitChatId === chat.chatId || permitChatId === chat._id) {
        return;
      }
    }
    const now = Date.now();
    const timeSinceLastNav = now - lastNavigationTimeRef.current;
    if (timeSinceLastNav < NAVIGATION_THROTTLE && pendingChatId) {
      return;
    }

    if (navigationTimerRef.current) {
      clearTimeout(navigationTimerRef.current);
      navigationTimerRef.current = null;
    }

    setPendingChatId(chat._id);

    navigationTimerRef.current = setTimeout(() => {
      lastNavigationTimeRef.current = Date.now();
      setActivePermitChatID(chat._id);
      if (isProjectClick) {
        // pass permit id to the chat page   
        navigate(`/citypermit/project/chat/${chat.chatId}`, { state: { permitId: chat.permitId } });

      } else {
        navigate(`/citypermit/project/chat/${chat._id}`, { state: { chat: chat } });
      }
      navigationTimerRef.current = null;
    }, 300);
  };

  const handleNewPermit = () => {
    navigate('/citypermit/project/new-chat');
  };

  const clearSearch = () => {
    setSearchQuery('');
  };

  return (
    <aside className="hidden md:flex w-64 bg-figma-background border-r border-border sticky h-full flex-col px-4 pt-4">
      <nav className='mb-10'>
        <ul className="flex flex-col gap-2">
          {navItems.map(item => {
            const Icon = item.icon;
            const isActive = !permitChatId && location.pathname.startsWith(item.path) && location.pathname !== '/citypermit/project/new-chat';

            if (item.label === 'New chat') {
              return (
                <li key={item.path}>
                  <button
                    type="button"
                    onClick={() => {
                      handleNewPermit();
                    }}
                    className={cn(
                      'flex w-full items-center gap-3 px-4 py-2 rounded-full text-sm transition',
                      isActive
                        ? 'bg-zinc-800 text-white'
                        : 'text-muted-foreground hover:bg-zinc-800/60 hover:text-white'
                    )}
                  >
                    <Icon size={20} />
                    <span>{item.label}</span>
                  </button>
                </li>
              );
            }

            return (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={cn(
                    'flex items-center gap-3 px-4 py-2 rounded-full text-sm transition',
                    isActive ? 'bg-zinc-800 text-white' : 'text-muted-foreground hover:bg-zinc-800/60 hover:text-white'
                  )}
                >
                  <Icon size={16} />
                  <span>{item.label}</span>
                </Link>
              </li>
            );
          })}

          <li className="py-1 px-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
              <input
                type="text"
                placeholder="Search chat"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-9 py-2 rounded-full text-sm dark bg-black  focus:outline-none text-white placeholder:text-muted-foreground transition"
              />
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white transition"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          </li>
        </ul>
      </nav>

      {/* <div className="w-full h-px bg-zinc-700 my-2" /> */}

      <div>
        {/* <div className="rounded-lg bg-gray-800/90 p-3 mb-4">
          <button
            onClick={() => setIsUnassignedCollapsed(!isUnassignedCollapsed)}
            className="flex items-center justify-between w-full text-yellow-400 text-xs mb-2"
          >
            <span>Unassigned Chats</span>
            {isUnassignedCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
          </button>

          {!isUnassignedCollapsed && (
            <div className="flex flex-col gap-1 max-h-[140px] overflow-y-auto no-scrollbar">
              {unassignedChats.length > 0 ? (
                unassignedChats.map(chat => (
                  <button
                    key={chat._id}
                    onClick={() => openChat(chat)}
                    className={cn(
                      'w-full text-left px-4 py-2 rounded-full text-sm transition',
                      'text-muted-foreground hover:bg-zinc-800 hover:text-white'
                    )}
                    title={chat.chatName}
                  >
                    <span className="block truncate">{chat.chatName}</span>
                  </button>
                ))
              ) : (
                <div className="text-center text-sm text-muted-foreground py-4">
                  No unassigned chats found
                </div>
              )}
              <InfiniteScrollPagination
                hasMore={hasMoreUnassigned}
                loading={unassignedLoading}
                onLoadMore={loadMoreUnassigned}
                loadingText="Loading..."
                className="py-1"
                showEndText={false}
              />
            </div>
          )}
        </div> */}

        {/* Your Projects (Grouped Chats) */}
        <div>
          {/* <button
            onClick={() => setIsProjectsCollapsed(!isProjectsCollapsed)}
            className="flex items-center justify-between w-full text-xs text-muted-foreground mb-2 px-1"
          >
            <span className="uppercase">YOUR PROJECTS</span>
            {isProjectsCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
          </button> */}

          <span className="text-sm text-muted-foreground mb-2 block px-4">Your Projects</span>

          <div className="border-t mb-2" />

          {!isProjectsCollapsed && (
            <div className="flex flex-col gap-1 max-h-[250px] overflow-y-auto no-scrollbar">
              {groupedProjects.filter(project => project.projectId !== null).map(project => {
                const isExpanded = expandedProjectId === project.projectId;
                return (
                  <div key={project.projectId} className="mb-2">
                    <button
                      onClick={() => setExpandedProjectId(isExpanded ? null : project.projectId)}
                      className={cn(
                        'flex items-center justify-between w-full px-4 py-2 rounded-full text-sm text-white transition',
                        isExpanded ? 'border border-zinc-600' : 'hover:bg-zinc-800/40'
                      )}
                    >
                      <span className="truncate">{project.projectName}</span>
                      {/* <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-600 text-white shrink-0">
                        {project.chats.length}
                      </span> */}
                    </button>

                    {isExpanded && (
                      <div className="mt-1 flex flex-col gap-1">
                        {project.chats.map(chat => {
                          const isActiveChat = permitChatId === chat.chatId;
                          return (
                            <div className="my-1 ml-4">
                              <button
                                key={chat.chatId}
                                onClick={() => {
                                  openChat(chat, true);
                                  setExpandedChatId(expandedChatId === chat.chatId ? null : chat.chatId);
                                }}
                                className="flex items-center gap-2 w-full text-left px-3 py-2 rounded-[15px] text-xs bg-[#212121] hover:bg-zinc-700 text-white transition"
                                title={chat.chatName}
                              >
                                <span
                                  className={cn('w-2 h-2 rounded-full shrink-0', isActiveChat ? 'bg-yellow-400' : 'bg-zinc-600')}
                                />
                                <span className="flex-1 block truncate">{chat.chatName}</span>
                                <span className="text-zinc-400 shrink-0"><MoveRight size={14} /></span>
                              </button>
                            </div>
                          );
                        })}
                        {/* <button
                          onClick={handleNewPermit}
                          className="flex items-center gap-2 w-full text-left px-3 py-2 rounded-lg text-xs border border-dashed border-zinc-600 text-zinc-400 hover:text-white hover:border-zinc-400 transition"
                        >
                          <Plus size={12} />
                          <span>Add Another Permit</span>
                        </button> */}
                      </div>
                    )}
                  </div>
                );
              })}

              <InfiniteScrollPagination
                hasMore={hasMoreProjects}
                loading={projectsLoading}
                onLoadMore={loadMoreGroupedProjects}
                loadingText="Loading..."
                className="py-2"
                showEndText={false}
              />
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 flex flex-col flex-1 min-h-0">
        <span className="text-sm text-muted-foreground mb-2 block px-4">Chats</span>

        <div className="border-t mb-2" />

        <div className="flex flex-col gap-1 overflow-y-auto flex-1 min-h-0 pr-2 py-2 no-scrollbar">
          {unassignedLoading && !unassignedChats.length ? (
            <div className="text-center text-sm text-muted-foreground py-4">Loading...</div>
          ) : unassignedChats.length > 0 ? (
            <>
              {unassignedLoading && (
                <div className="text-xs text-muted-foreground px-4 py-1">Searching...</div>
              )}
              {unassignedChats.map(chat => {
                const isActiveChat = permitChatId === chat._id;
                const isPending = pendingChatId === chat._id;
                const isSelected = isActiveChat || isPending;

                return (
                  <button
                    key={chat._id}
                    onClick={() => openChat(chat, false)}
                    className={cn(
                      'w-full text-left px-4 py-2 rounded-full text-sm transition',
                      isSelected
                        ? 'bg-zinc-800 text-white'
                        : 'text-muted-foreground hover:bg-zinc-800/60 hover:text-white'
                    )}
                    title={chat.chatName}
                  >
                    <span className="block truncate">{chat.chatName}</span>
                  </button>
                );
              })}
            </>
          ) : (
            <div className="text-center text-sm text-muted-foreground py-4">
              {unassignedLoading ? 'Searching...' : searchQuery ? 'No chats found' : 'No chats yet'}
            </div>
          )}
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
